import React, { useState } from 'react';
import { useHistory } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import { Input, Button, ErrorSummary } from 'govuk-react-jsx';
import { checkValidPassword } from '../../regex/regex';
import {
  setPmcPasswordValue,
  setPmcRepeatPasswordValue,
  getPmcPasswordValue,
  getPmcRepeatPasswordValue,
} from '../../features/pmc/pmcJourneySlice';
import {
  getCurrentLanguage,
  getCopyProviderCY,
  getCopyProviderEN,
} from '../../features/app/appSlice';
import { createPassword, reauthenticate } from '../../firebase';
import useTitle from '../../hooks/useTitle';

const CreatePassword = () => {
  const passwordValueRedux = useSelector(getPmcPasswordValue);
  const repeatPasswordValueRedux = useSelector(getPmcRepeatPasswordValue);
  const [errorMsgPassword, setErrorMsgPassword] = useState('');
  const [errorMsgRepeatPassword, setErrorMsgRepeatPassword] = useState('');
  const [errorList, setErrorList] = useState([]);
  const history = useHistory();
  const dispatch = useDispatch();
  const currentLanguage = useSelector(getCurrentLanguage);
  const copyProviderCY = useSelector(getCopyProviderCY);
  const copyProviderEN = useSelector(getCopyProviderEN);
  const copyProvider =
    currentLanguage === 'en' ? copyProviderEN : copyProviderCY;

  useTitle(
    'landlordReg.CreatePassword.s9',
    'Create your password - OZEV Electric Vehicle Chargepoint Grant - GOV.UK'
  );

  const updatedPasswordValue = (event) => {
    dispatch(setPmcPasswordValue(event.target.value));
  };

  const updatedRepeatPasswordValue = (event) => {
    dispatch(setPmcRepeatPasswordValue(event.target.value));
  };

  const errorMsg = copyProvider.getCopy(
    'landlordReg.CreatePassword.s1',
    'Create a password that meets the above mentioned criteria'
  );

  const errorMsgMatch = copyProvider.getCopy(
    'landlordReg.CreatePassword.s2',
    'Passwords do not match'
  );

  const handleBtnClick = () => {
    let showError;
    if (
      !checkValidPassword.test(passwordValueRedux) &&
      !checkValidPassword.test(repeatPasswordValueRedux)
    ) {
      showError = {
        children: errorMsg,
      };
      setErrorMsgPassword(showError);
      setErrorMsgRepeatPassword(showError);
      setErrorList([
        { ...showError, href: '#input-with-error-message-create-password' },
        { ...showError, href: '#input-with-error-message-repeat-password' },
      ]);
    } else if (
      checkValidPassword.test(passwordValueRedux) &&
      !checkValidPassword.test(repeatPasswordValueRedux)
    ) {
      showError = {
        children: errorMsg,
      };
      setErrorMsgPassword(null);
      setErrorMsgRepeatPassword(showError);
      setErrorList([
        { ...showError, href: '#input-with-error-message-create-password' },
      ]);
    } else if (
      !checkValidPassword.test(passwordValueRedux) &&
      checkValidPassword.test(repeatPasswordValueRedux)
    ) {
      showError = {
        children: errorMsg,
      };
      setErrorMsgPassword(showError);
      setErrorMsgRepeatPassword(null);
      setErrorList([
        { ...showError, href: '#input-with-error-message-create-password' },
      ]);
    } else if (
      checkValidPassword.test(passwordValueRedux) &&
      checkValidPassword.test(repeatPasswordValueRedux)
    ) {
      if (passwordValueRedux !== repeatPasswordValueRedux) {
        showError = {
          children: errorMsgMatch,
        };
        setErrorMsgPassword(showError);
        setErrorMsgRepeatPassword(showError);
        setErrorList([
          { ...showError, href: '#input-with-error-message-create-password' },
          { ...showError, href: '#input-with-error-message-repeat-password' },
        ]);
      } else {
        createPassword({ password: passwordValueRedux })
          .then((response) => {
            const { data } = response;
            const { temporaryEmail } = data;
            reauthenticate(temporaryEmail, passwordValueRedux);

            setErrorMsgPassword(null);
            setErrorMsgRepeatPassword(null);
            dispatch(setPmcPasswordValue(passwordValueRedux));
            dispatch(setPmcRepeatPasswordValue(repeatPasswordValueRedux));
            history.push('/pmcReg/password-created');
          })
          .catch(() => {
            // TODO: add error when the server is not responding
            // TODO: add error if changing the password failed
          });
      }
    }
  };
  const pageModel = (
    <>
      {errorList?.length > 0 && (
        <ErrorSummary
          errorList={errorList}
          titleChildren={copyProvider.getCopy(
            'Common.error-summery',
            'There is a problem'
          )}
        />
      )}
      <h1 className="govuk-heading-xl">
        {copyProvider.getCopy(
          'landlordReg.CreatePassword.s3',
          'Create your password'
        )}
      </h1>
      <p className="govuk-body">
        {copyProvider.getCopy(
          'landlordReg.CreatePassword.s4',
          'This is the password you will use to login and access your account.'
        )}
      </p>
      <div className="govuk-form-group">
        <Input
          errorMessage={errorMsgPassword}
          label={{
            children: copyProvider.getCopy(
              'landlordReg.CreatePassword.s5',
              'Create a password'
            ),
          }}
          hint={{
            children: [
              <div className="govuk-!-margin-bottom-1">
                {copyProvider.getCopy(
                  'landlordReg.CreatePassword.s6',
                  'Your password must:'
                )}
              </div>,
              <ul className="govuk-list govuk-list--bullet govuk-hint">
                <li>
                  {copyProvider.getCopy(
                    'landlordReg.CreatePassword.s6a',
                    'be at least 8 characters or longer'
                  )}
                </li>
                <li>
                  {copyProvider.getCopy(
                    'landlordReg.CreatePassword.s6b',
                    'include at least 1 uppercase letter'
                  )}
                </li>
                <li>
                  {copyProvider.getCopy(
                    'landlordReg.CreatePassword.s6c',
                    'include at least 1 lowercase letter'
                  )}
                </li>
                <li>
                  {copyProvider.getCopy(
                    'landlordReg.CreatePassword.s6d',
                    'include at least 1 number'
                  )}
                </li>
                <li>
                  {copyProvider.getCopy(
                    'landlordReg.CreatePassword.s6e',
                    'include a symbol'
                  )}
                </li>
              </ul>,
            ],
          }}
          id="input-with-error-message-create-password"
          className="govuk-input govuk-!-width-two-thirds"
          name="test-name-3"
          type="password"
          value={passwordValueRedux}
          onChange={(event) => updatedPasswordValue(event)}
        />

        <Input
          errorMessage={errorMsgRepeatPassword}
          id="input-with-error-message-repeat-password"
          label={{
            children: copyProvider.getCopy(
              'landlordReg.CreatePassword.s7',
              'Repeat your password'
            ),
          }}
          className="govuk-input govuk-!-width-two-thirds"
          name="test-name-3"
          type="password"
          value={repeatPasswordValueRedux}
          onChange={(event) => updatedRepeatPasswordValue(event)}
        />
      </div>
      <Button className="govuk-button" onClick={handleBtnClick}>
        {copyProvider.getCopy('landlordReg.CreatePassword.s8', 'Continue')}
      </Button>
    </>
  );
  return pageModel;
};

export default CreatePassword;
